import { useEffect } from 'react';
import { JobListSelectorProps } from '@type/bulk-operations';
import { ApiBulkSelectJobList } from '@type/v1-api-types/bulk-operations';
import { useTranslation } from 'next-i18next';
import { useSwrSelectJobList } from '@hooks/useSwrApi/endpoints/bulk-operations';
import {
  ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption, Spinner,
} from '@components/common';
import { Input, Button, ListboxWrapper } from '@components/common/Forms';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { filterStyles } from '@components/features/filters/helpers/filterStyles';
import { i18nConfig } from 'i18n-config';


/**
 * @description The job list selector component. It's a stylized ListboxWrapper component.
 * - ***record** - the record function from useBjForm.
 * - ***locale** - the locale. Defaults to 'ro'.
 * - ***showJobList** - boolean value that indicates if the job list or new job title input should be shown.
 * - ***setShowJobList** - function that sets the showJobList value.
 * - ***updateFormElements** - function that updates the form elements.
 */
export const JobListSelector = (props: JobListSelectorProps) => {
  const {
    record,
    locale = i18nConfig.defaultLocale,
    showJobList,
    setShowJobList,
    updateFormElements,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // SWR: Get company job list. Also update the form elements when the job list changes
  const {
    data,
    isLoading,
  } = useSwrSelectJobList(locale, '', updateFormElements);

  // The initial value for the destination job index
  const findInitialDestinationJobIndex = () => data?.findIndex((job) => job.selected === true) || 0;


  // Show the switch buttons based on the job list in parent component. Remove the undefined value.
  useEffect(() => {
    if (showJobList === undefined && data && data.length === 0) setShowJobList(false);
    if (showJobList === undefined && data && data.length > 0) setShowJobList(true);
  }, [data, showJobList, setShowJobList]);


  // Render component
  // **************************************
  return (
    <>
      { /* Spinner */
        !data && isLoading && <div className="h-20"><Spinner /></div>
      }

      { /* Select a job from the existing ones */
        showJobList && data && data.length > 0 && (
          <>
            <p className="mb-2.5 text-sm">
              {t('cv.action.label.existing_jobs')}
            </p>

            <ListboxWrapper
              data={data}
              fieldName="destinationJobId"
              defaultValueKey="jobId"
              labelKey="jobTitle"
              initialSelectedIndex={[findInitialDestinationJobIndex() > 0 ? findInitialDestinationJobIndex() : 0]}
              recordFunc={record}
              renderListbox={({
                selectedLabel, lsData, selected, setSelected,
              }) => (
                <ListboxStyled className="relative" by="jobId" value={selected as ApiBulkSelectJobList[number]} onChange={setSelected}>
                  <>
                    <ListboxStyledButton
                      as={Button}
                      color="light"
                      styling="outline"
                      className="w-full !justify-between !border-input !bg-surface"
                    >
                      <span className="text-left">{selectedLabel || ''}</span>
                      <ChevronDownIcon className="-mr-2 ml-1.5 size-5" />
                    </ListboxStyledButton>

                    <ListboxStyledOptions
                      className={`${filterStyles.listboxOptionsWrapper} !max-h-72 !w-full py-2`}
                      animation="slideDown"
                    >
                      {lsData.map((field) => (
                        <ListboxStyledOption
                          key={field.jobId}
                          value={field}
                          className={`${filterStyles.listboxOption} gap-1.5`}
                        >
                          <CheckIcon className={filterStyles.listboxOptionCheck} />
                          {field.jobTitle}
                        </ListboxStyledOption>
                      ))}
                    </ListboxStyledOptions>
                  </>
                </ListboxStyled>
              )}
            />
          </>
        )
      }

      { /* Add a new job also if there are no current jobs */
        showJobList === false && (
          <>
            <p className="mb-2.5 text-sm">
              {t('cv.action.new')}
            </p>
            <Input
              required
              type="text"
              className="asOptional"
              {...record('destinationJobTitle')}
            />
          </>
        )
      }
    </>
  );
};
